<template>
  <div>
    <AppDialog
      :show="!showConfirm"
      title="Choose main member to merge into"
      class="merge-members-dialog"
      size="large"
      append-to-body
      data-testid="merge-manual-dialog"
      @close="close"
    >
      <div class="mb-30">
        <h5 class="mb-10">Selected duplicate member</h5>
        <div
          class="border-rounded px-15 d-inline-flex align-items-center gap-10 border-solid py-10"
          data-testid="merge-manual-duplicate"
        >
          <HubAvatar
            :user-id="duplicateUser.user.id"
            avatar-style="medium"
            :users-avatars="usersAvatars"
          />
          <div>
            <div
              class="font-weight-bold"
              data-testid="merge-manual-duplicate-name"
            >
              {{ duplicateUser.user.name }}
            </div>
            <div class="small text-lighter">{{ duplicateOsUsername }}</div>
            <div class="small text-lighter">{{ duplicateUser.user.email }}</div>
            <div class="small text-lighter">{{ duplicateUser.member_type }} member</div>
          </div>
        </div>
      </div>

      <div class="mb-30 d-flex align-items-center gap-5">
        <h4 class="font-weight-bold m-0">Merge into</h4>
        <i class="hi hi-right-arrow hi-12 text-lighter ml-10"></i>
      </div>

      <h5 class="mb-10">
        Select a suggested member or use search to find a member. Only 1 member can be merged into at a time.
      </h5>

      <div class="form-group has-icon-left border-radius-small mb-15 max-w-30rem">
        <AppInput
          v-model="search"
          type="search"
          placeholder="Search full name, username, or email"
          data-testid="merge-manual-search-input"
          @input="handleInput"
        />
        <i class="hi hi-search"></i>
        <i
          v-if="search"
          style="left: auto !important; right: 15px"
          class="hi hi-close hi-10 input-clear clickable"
          @click="resetSearch"
        ></i>
      </div>

      <div>
        <h6
          class="mb-5 text-grey"
          data-testid="merge-manual-message"
        >
          <span v-if="searching">{{ searchMessage }}</span>
          <span v-else>{{ suggestedMessage }}</span>
        </h6>

        <div class="table-scroll-wrapper mb-10">
          <ClientAgentMergeCandidatesTable
            v-if="!emptyState || loading"
            :candidates="displayedCandidates"
            :pagination="pagination"
            :selected-id.sync="selectedId"
            :error="showError"
            :searching="searching"
            :loading="loading"
            @page-changed="onPageChanged"
          />
        </div>

        <div
          v-if="emptyState"
          class="py-20 text-center merge-empty-state"
        >
          <img
            :src="IMAGE_EMPTY_BOX"
            class="mb-12"
            alt="no results"
          />
          <h5>No {{ searching ? 'results found' : 'suggested members' }}</h5>
          <p class="text-muted">
            <template v-if="searching">
              We couldn't find any members that match your<br />
              search criteria.
            </template>
            <template v-else>
              We can't find any suggested matches to<br />
              this full name, username, or email. Try searching for a<br />
              member to merge into.
            </template>
          </p>

          <AppButton
            v-if="searching && suggestedCount"
            type="default"
            size="small"
            @click="resetSearch"
          >
            See {{ `${suggestedCount} suggested ${pluralize(suggestedCount, 'match', 'matches')}` }}
          </AppButton>
        </div>

        <span
          v-if="showError"
          class="small text-danger"
          data-testid="merge-manual-selection-error"
        >
          Select 1 main member to merge the duplicate into
        </span>
      </div>

      <template #footer>
        <AppButton
          data-testid="merge-dialog-cancel"
          @click="close"
        >
          Cancel
        </AppButton>
        <AppButton
          :disabled="mergeDisabled"
          type="primary"
          data-testid="merge-manual-merge-button"
          @click="submitMerge"
        >
          Merge
        </AppButton>
      </template>
    </AppDialog>

    <ClientAgentMergeConfirmDialog
      v-if="showConfirm"
      :duplicate="duplicateUser"
      :main="mainMember"
      @cancel="showConfirm = false"
      @confirm="handleConfirm"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import { OrganizationsResource } from '~/resources';
import { IMAGE_EMPTY_BOX } from '~/rails_assets';
import ClientAgentMergeCandidatesTable from './ClientAgentMergeCandidatesTable.vue';
import ClientAgentMergeConfirmDialog from './ClientAgentMergeConfirmDialog.vue';
import { pluralize } from '~/utils/string_utils';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';

export default {
  name: 'ClientAgentManualMergeDialog',
  components: { ClientAgentMergeCandidatesTable, ClientAgentMergeConfirmDialog },
  mixins: [createUsersAvatarsMixin('avatarUsers')],
  props: {
    duplicateUser: { type: Object, required: true },
  },
  data() {
    return {
      search: '',
      candidates: [],
      suggested: [],
      loading: false,
      page: 1,
      perPage: 20,
      paginationPayload: null,
      selectedId: null,
      showError: false,
      showConfirm: false,
      mainMember: null,
      debouncedFetch: null,
      IMAGE_EMPTY_BOX,
    };
  },
  computed: {
    searching() {
      return this.search.trim().length > 0;
    },
    displayedCandidates() {
      return this.searching ? this.candidates : this.suggested;
    },
    emptyState() {
      return !this.displayedCandidates.length && !this.loading;
    },
    mergeDisabled() {
      return this.displayedCandidates.length === 0;
    },
    suggestedCount() {
      return this.suggested.length;
    },
    duplicateOsUsername() {
      return this.duplicateUser?.client_agent?.os_username;
    },
    avatarUsers() {
      const arr = [];

      if (this.duplicateUser?.user?.avatar_url) {
        arr.push({ id: this.duplicateUser.user.id, avatar: this.duplicateUser.user.avatar_url });
      }
      if (this.mainMember?.user?.avatar_url) {
        arr.push({ id: this.mainMember.user.id, avatar: this.mainMember.user.avatar_url });
      }

      return arr;
    },
    searchMessage() {
      if (!this.searching) {
        return '';
      }

      if (this.loading) {
        return 'Searching...';
      }

      const total = this.paginationPayload?.total_count;
      const count = (total ?? this.displayedCandidates.length);

      return count ? `Search results (${count})` : 'No results found';
    },
    suggestedMessage() {
      return this.suggestedCount > 0
        ? `Suggested members (${this.suggestedCount})`
        : 'No suggested members';
    },
    pagination() {
      return this.paginationPayload || { current_page: this.page, total_pages: 1 };
    }
  },
  watch: {
    selectedId(val) {
      if (!val) {
        this.mainMember = null;

        return;
      }

      const candidate = [...this.suggested, ...this.candidates].find((c) => c.id === val);

      if (candidate) {
        this.mainMember = candidate;
      }
    },
  },

  mounted() {
    this.fetchCandidates();
    this.debouncedFetch = debounce((val) => {
      this.fetchCandidates(val);
    }, 300);
  },
  methods: {
    pluralize,
    close() {
      this.$emit('close');
    },
    fetchCandidates(search = '') {
      if (search.trim()) {
        this.candidates = [];
      } else {
        this.suggested = [];
      }

      this.loading = true;
      const params = {
        duplicate_user_id: this.duplicateUser.user.id,
        page: this.page,
        per_page: this.perPage,
        ...(search && { search }),
      };

      OrganizationsResource.mergeSuggestions({ id: this.$store.getters.organizationId }, params)
        .then((res) => {
          this.paginationPayload = res.pagination || null;
          if (search) {
            this.candidates = res.candidates || [];
          } else {
            this.suggested = res.candidates || [];
          }
        })
        .finally(() => (this.loading = false));
    },
    handleInput() {
      this.page = 1;

      const term = this.search.trim();

      if (term) {
        this.candidates = [];
      } else {
        this.suggested = [];
      }
      this.showError = false;
      this.loading = true;

      this.debouncedFetch(term);
    },
    onPageChanged(page) {
      this.page = page;
      this.fetchCandidates(this.search.trim());
    },
    resetSearch() {
      this.search = '';
      this.showError = false;
      this.page = 1;
      this.selectedId = null;
      this.mainMember = null;
      this.fetchCandidates();
    },
    submitMerge() {
      if (!this.selectedId) {
        this.showError = true;

        return;
      }

      if (!this.mainMember) {
        this.mainMember = [...this.suggested, ...this.candidates].find((c) => c.id === this.selectedId);
      }
      this.showConfirm = true;
    },
    handleConfirm() {
      this.$emit('confirm-merge', {
        fromIds: [this.duplicateUser.user.id],
        toId: this.mainMember.user.id,
      });
      this.close();
    },
  },
};
</script>
